import React, { useMemo, useState } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { R_VALUES, rSortKey } from './coverage-widgets/RValueCoverage';

// IDI-506 D1: the blow-in chart's ladder plus R-14 and R-16 (the chart itself
// is unchanged), plus the item's own value, in number order.
const SHEET_EXTRAS = ['R-14', 'R-16'];
const NONE_LABEL = 'No R-value';

function rValueOptions(own) {
  const set = new Set([...R_VALUES, ...SHEET_EXTRAS]);
  if (own && own !== '–') set.add(own);
  return Array.from(set).sort((a, b) => rSortKey(a) - rSortKey(b));
}

// IDI-506 D1: "r33", "R33", "R-33" -> "R-33" (the chart's addRValue rule).
function normaliseRValue(text) {
  const n = String(text || '').trim().replace(/^r-?/i, '');
  if (!/^\d+(\.\d+)?[A-Za-z]*$/.test(n)) return null;
  return `R-${n.toUpperCase()}`;
}

/**
 * Searchable R-Value list for the Item Catalog side sheet. Typing filters the
 * list; an unlisted value offers 'Add R-value "R-n"', which applies to this
 * item only (it lives in the item's own value). `optional` adds a leading
 * "No R-value" option that saves as ''.
 */
export default function RValuePicker({ value, onChange, optional, label }) {
  const [query, setQuery] = useState('');
  const options = useMemo(() => rValueOptions(value), [value]);
  // IDI-683 D3: "R30" / "r30" search as "R-30".
  const q = query.trim().toLowerCase().replace(/^r(?=\d)/, 'r-');
  const filtered = q ? options.filter((r) => r.toLowerCase().includes(q)) : options;
  const typed = normaliseRValue(query);
  const offerAdd = typed && !options.includes(typed);
  const current = value && value !== '–' ? value : '';

  return (
    <Combobox
      value={current}
      onChange={(v) => { onChange(v == null ? '' : v); setQuery(''); }}
      immediate
    >
      <div className="relative">
        <ComboboxInput
          aria-label={label}
          className="w-full px-3 py-2 text-sm"
          style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}
          displayValue={(v) => v || (optional ? NONE_LABEL : '')}
          placeholder={optional ? NONE_LABEL : 'Select R-value…'}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={() => setQuery('')}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2" aria-label={`Show ${label} options`}>
          <ChevronUpDownIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
        </ComboboxButton>
        <ComboboxOptions
          className="absolute z-20 top-full mt-1 w-full bg-white border max-h-48 overflow-y-auto text-sm"
          style={{ borderColor: 'var(--color-border)', borderRadius: 'var(--radius-md)' }}
        >
          {optional && !q && (
            <ComboboxOption value="" className="px-3 py-1.5 cursor-pointer data-[focus]:bg-gray-100">
              {NONE_LABEL}
            </ComboboxOption>
          )}
          {filtered.map((r) => (
            <ComboboxOption key={r} value={r} className="px-3 py-1.5 cursor-pointer data-[focus]:bg-gray-100">
              {r}
            </ComboboxOption>
          ))}
          {offerAdd && (
            <ComboboxOption value={typed} className="px-3 py-1.5 cursor-pointer data-[focus]:bg-gray-100">
              {`Add R-value "${typed}"`}
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
